import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { Button, useBreakpoint } from "@avenue-8/sidekick-ui";
import type { ToastPosition } from "react-toastify";
import {
  getContactQueryKey,
  listContactsQueryKey,
  listSuggestionsQueryKey,
  useSetContactMonitoring,
  type ContactDetailResponseDto,
  type ContactResponseDto,
  type ListContacts200,
} from "@shared/generated/crm";
import type { GetConsumption200 } from "@shared/generated/radar";
import { getConsumptionQueryKey } from "@shared/generated/radar/queries/contactsController/useGetConsumption";
import { toast } from "@shared/services/toast/toast";
import { getContactRadarSignalsPath } from "@shared/utils/contact-briefing-links";

const nonNegative = (value: number) => Math.max(0, value);

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const ViewProgressButton = styled(Button)`
  flex-shrink: 0;
  min-width: 0;
  padding: 0.25rem 0.75rem;
  font-size: ${(p) => p.theme.text.fontSizes.s};
`;

function showMonitoringStartedToast({
  toastId,
  contactName,
  position,
  onViewClick,
}: {
  toastId: string;
  contactName: string;
  position: ToastPosition;
  onViewClick: () => void;
}) {
  toast.success(
    <ToastContent>
      <span>
        Monitoring started for {contactName}. Radar will look for their social
        profiles and begin researching their activity.
      </span>
      <ViewProgressButton
        variant="text"
        size="small"
        onClick={() => {
          onViewClick();
          toast.dismiss(toastId);
        }}
      >
        View
      </ViewProgressButton>
    </ToastContent>,
    { toastId, position, autoClose: 8000 },
  );
}

function useContactMonitoringMutation<TQueryData>({
  queryKey,
  readIsMonitoredSince,
  writeIsMonitoredSince,
  writeContact,
}: {
  queryKey: QueryKey;
  readIsMonitoredSince: (data: TQueryData, contactId: string) => string | null;
  writeIsMonitoredSince: (
    data: TQueryData,
    contactId: string,
    isMonitoredSince: string | null,
  ) => TQueryData;
  writeContact: (
    data: TQueryData,
    contactId: string,
    updatedContact: ContactResponseDto,
  ) => TQueryData;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setContactMonitoring = useSetContactMonitoring();
  const [pendingContactIds, setPendingContactIds] = useState<Set<string>>(
    new Set(),
  );
  const isMobile = useBreakpoint("mobile");
  const toastPosition: ToastPosition = isMobile
    ? "bottom-center"
    : "bottom-right";

  const toggleMonitoring = useCallback(
    (contactId: string, contactName: string, nextValue: boolean) => {
      if (pendingContactIds.has(contactId)) return;
      setPendingContactIds((prev) => new Set(prev).add(contactId));

      const currentData = queryClient.getQueryData<TQueryData>(queryKey);
      const previousIsMonitoredSince = currentData
        ? readIsMonitoredSince(currentData, contactId)
        : null;
      const consumptionDelta = nextValue ? 1 : -1;

      queryClient.setQueryData<TQueryData>(
        queryKey,
        (old) =>
          old &&
          writeIsMonitoredSince(
            old,
            contactId,
            nextValue ? new Date().toISOString() : null,
          ),
      );
      queryClient.setQueryData<GetConsumption200>(
        getConsumptionQueryKey(),
        (old) =>
          old && {
            ...old,
            count: nonNegative(old.count + consumptionDelta),
            remaining: nonNegative(old.remaining - consumptionDelta),
          },
      );

      setContactMonitoring.mutate(
        { contactId, data: { isMonitored: nextValue } },
        {
          onSuccess: (updatedContact) => {
            queryClient.setQueryData<TQueryData>(
              queryKey,
              (old) => old && writeContact(old, contactId, updatedContact),
            );
            queryClient.invalidateQueries({
              queryKey: getConsumptionQueryKey(),
            });
            // Reconciles any active filtered list view (e.g. Radar only).
            queryClient.invalidateQueries({
              queryKey: listContactsQueryKey(),
            });
            if (nextValue) {
              queryClient.invalidateQueries({
                queryKey: listSuggestionsQueryKey(),
              });
              showMonitoringStartedToast({
                toastId: `radar-monitoring-started-${contactId}`,
                contactName,
                position: toastPosition,
                onViewClick: () =>
                  navigate(getContactRadarSignalsPath(contactId)),
              });
            } else {
              toast(`Monitoring stopped for ${contactName}.`, {
                toastId: `radar-monitoring-stopped-${contactId}`,
                position: toastPosition,
              });
            }
          },
          onError: () => {
            queryClient.setQueryData<TQueryData>(
              queryKey,
              (old) =>
                old &&
                writeIsMonitoredSince(old, contactId, previousIsMonitoredSince),
            );
            queryClient.setQueryData<GetConsumption200>(
              getConsumptionQueryKey(),
              (old) =>
                old && {
                  ...old,
                  count: nonNegative(old.count - consumptionDelta),
                  remaining: nonNegative(old.remaining + consumptionDelta),
                },
            );
            toast.error(
              nextValue
                ? `Couldn't start monitoring for ${contactName}. Please try again.`
                : `Couldn't stop monitoring for ${contactName}. Please try again.`,
              { position: toastPosition },
            );
          },
          onSettled: () => {
            setPendingContactIds((prev) => {
              const next = new Set(prev);
              next.delete(contactId);
              return next;
            });
          },
        },
      );
    },
    [
      pendingContactIds,
      queryClient,
      navigate,
      toastPosition,
      queryKey,
      setContactMonitoring,
      readIsMonitoredSince,
      writeIsMonitoredSince,
      writeContact,
    ],
  );

  return { toggleMonitoring, pendingContactIds };
}

export function useListContactMonitoringToggle({
  queryKey,
}: {
  queryKey: QueryKey;
}) {
  return useContactMonitoringMutation<ListContacts200>({
    queryKey,
    readIsMonitoredSince: (data, contactId) =>
      data.items.find((c) => c.id === contactId)?.isMonitoredSince ?? null,
    writeIsMonitoredSince: (data, contactId, isMonitoredSince) => ({
      ...data,
      items: data.items.map((c) =>
        c.id === contactId ? { ...c, isMonitoredSince } : c,
      ),
    }),
    writeContact: (data, contactId, updatedContact) => ({
      ...data,
      items: data.items.map((c) => (c.id === contactId ? updatedContact : c)),
    }),
  });
}

export function useContactDetailMonitoringToggle({
  contactId,
  contactName,
}: {
  contactId: string;
  contactName: string;
}) {
  const { toggleMonitoring, pendingContactIds } =
    useContactMonitoringMutation<ContactDetailResponseDto>({
      queryKey: getContactQueryKey(contactId),
      readIsMonitoredSince: (data) => data.isMonitoredSince,
      writeIsMonitoredSince: (data, _contactId, isMonitoredSince) => ({
        ...data,
        isMonitoredSince,
      }),
      writeContact: (data, _contactId, updatedContact) => ({
        ...data,
        isMonitoredSince: updatedContact.isMonitoredSince,
        links: updatedContact.links,
      }),
    });

  return {
    toggleMonitoring: (nextValue: boolean) =>
      toggleMonitoring(contactId, contactName, nextValue),
    isPending: pendingContactIds.has(contactId),
  };
}
